import { Coordinate, Trip } from "@fairfleet/geotab";
import { useMemo } from "react";
import { useAddresses } from "@/hooks/addresses/useAddresses";

/** Where one trip started and ended. */
export interface TripAddresses {
  start: string;
  stop: string;
}

export interface TripAddressLookup {
  /**
   * Whether the addresses belong to the trips currently on show.
   *
   * The address query keeps the previous result while a new one loads, so without this the rows
   * would be labelled with the addresses of whatever was on screen before — and the export would
   * carry them.
   */
  isReady: boolean;
  byTripId: Map<string, TripAddresses>;
}

const NO_ADDRESSES: TripAddresses = { start: "", stop: "" };

/**
 * Where each trip started and ended, by address.
 *
 * A trip records only where it stopped, so where it started is where the same vehicle's previous
 * trip stopped. Resolved for the whole list in one batched request rather than per row: the export
 * reads these values, and per-row lookups would put into the file whatever had arrived by the time
 * someone pressed the button.
 *
 * @param trips - The trips on show.
 */
export function useTripAddresses(trips: Trip[]): TripAddressLookup {
  const previousStopByTripId = useMemo(() => previousStopPoints(trips), [trips]);

  // Keyed by coordinate rather than by trip: a vehicle standing at the same depot every evening,
  // and a fleet sharing that depot, ask about one point rather than hundreds.
  const pointsByKey = useMemo(() => {
    const points = new Map<string, Coordinate>();

    for (const trip of trips) {
      const point = trip.stopPoint as Coordinate | undefined;
      const key = coordinateKey(point);
      if (key && point) {
        points.set(key, point);
      }
    }

    return points;
  }, [trips]);

  const points = useMemo(() => [...pointsByKey.values()], [pointsByKey]);
  const { data: addresses, isPlaceholderData } = useAddresses(points);

  return useMemo(() => {
    const isReady = addresses !== undefined && !isPlaceholderData;
    if (!isReady) {
      return { isReady, byTripId: new Map<string, TripAddresses>() };
    }

    const addressByKey = new Map<string, string>();
    [...pointsByKey.keys()].forEach((key, index) => {
      const address = addresses[index]?.formattedAddress;
      if (address) {
        addressByKey.set(key, address);
      }
    });

    const byTripId = new Map<string, TripAddresses>();
    for (const trip of trips) {
      byTripId.set(trip.id, {
        start: lookUp(addressByKey, previousStopByTripId.get(trip.id)),
        stop: lookUp(addressByKey, trip.stopPoint as Coordinate | undefined),
      });
    }

    return { isReady, byTripId };
  }, [trips, pointsByKey, previousStopByTripId, addresses, isPlaceholderData]);
}

/** What a trip with no resolved addresses looks like, so callers need no null checks. */
export function tripAddressesOf(lookup: TripAddressLookup, tripId: string): TripAddresses {
  return lookup.byTripId.get(tripId) ?? NO_ADDRESSES;
}

/**
 * Where each trip's vehicle stopped the time before.
 *
 * Taken from Geotab's own link between consecutive trips — a trip's predecessor is the one whose
 * `nextTripStart` is this trip's `start`, which is how `usePreviousTrip` identifies it too. Not
 * from the position in the list: the list is filtered by reason in the browser, so the entry
 * before a trip is often not the trip before it.
 *
 * A vehicle's earliest trip in the chosen period has no predecessor in the list, so it has no
 * start address. Resolving that needs a query beyond the period.
 */
export function previousStopPoints(trips: Trip[]): Map<string, Coordinate> {
  const stopPointByFollowingTrip = new Map<string, Coordinate>();

  for (const trip of trips) {
    const deviceId = trip.device?.id;
    const point = trip.stopPoint as Coordinate | undefined;
    if (deviceId && point && trip.nextTripStart) {
      stopPointByFollowingTrip.set(linkKey(deviceId, trip.nextTripStart), point);
    }
  }

  const previous = new Map<string, Coordinate>();

  for (const trip of trips) {
    const deviceId = trip.device?.id;
    if (!deviceId || !trip.start) {
      continue;
    }
    const point = stopPointByFollowingTrip.get(linkKey(deviceId, trip.start));
    if (point) {
      previous.set(trip.id, point);
    }
  }

  return previous;
}

function linkKey(deviceId: string, at: Date): string {
  return `${deviceId}|${at.getTime()}`;
}

function coordinateKey(point: Coordinate | undefined): string | undefined {
  return point ? `${point.x},${point.y}` : undefined;
}

function lookUp(addressByKey: Map<string, string>, point: Coordinate | undefined): string {
  const key = coordinateKey(point);
  return (key && addressByKey.get(key)) ?? "";
}
